import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import Badge from './Badge'

/**
 * One grantmaker from CORPUS search — a different object from FunderCard's.
 *
 * Deliberately NOT a variant of FunderCard. That card renders a `FunderMatchOut`
 * and leads with a score, a why-matched blurb and feedback thumbs, all of which
 * require a FunderMatch row. Most results here have none: that is the point of
 * corpus search. Bending one component across both shapes would mean a score
 * slot rendering blank, feedback buttons that 404, and a growing pile of
 * `funder.score ? … : …` — the two surfaces would drift anyway, just less
 * visibly.
 *
 * What leads instead is `why_not`: whether this foundation is among the
 * client's leads and, if not, why. That is the question the page exists to
 * answer, and the one a competitor with no opinion about the client cannot.
 *
 * Every result links to /funders/:id. The profile endpoint now serves any
 * corpus funder to Pro+ (ADR-0012), and only Pro+ ever sees this card at all —
 * lower tiers get the 403 upsell before a result is rendered — so there is no
 * shape of this page that can produce a link into a 404.
 */

// Maps a why_not reason code to how the badge should read. Codes are stable
// server-side (backend/api/corpus.py REASON_*); an unrecognised one falls back
// to neutral styling rather than throwing away the explanation.
// `variant` values are Badge's own (urgent | soon | open | default | indigo) —
// not free-form colour names, which would silently fall back to default.
const REASON_STYLE = {
  matched: { variant: 'open', label: 'In your matches' },
  // Both exclusion classes read as "Not in your digest" without these — true,
  // but it throws away the only part a grant writer can act on: the reason no
  // amount of scoring would ever surface this funder. The full sentence is in
  // the expanded explanation; these are the badge.
  donor_advised_intermediary: { variant: 'default', label: 'Donor-advised fund' },
  scholarship_fund: { variant: 'default', label: 'Scholarships for students' },
  // ADR-0041: its filing sends most of its money to an organisation of its own
  // name, so there is nobody else to apply.
  affiliate_passthrough: { variant: 'default', label: 'Funds its own affiliate' },
  // ADR-0043: >=70% of its filing into a DAF or another family foundation.
  pays_giving_vehicle: { variant: 'default', label: 'Pays into another fund' },
  not_open: { variant: 'default', label: 'Not accepting applications' },
  stale_filing: { variant: 'default', label: 'Older filing' },
  no_giving_recorded_in_your_state: {
    variant: 'default',
    label: 'No giving in your state',
  },
  below_match_threshold: { variant: 'default', label: 'Below match threshold' },
}

function CorpusFunderCard({ funder }) {
  const [open, setOpen] = useState(false)

  const location = [funder.city, funder.state].filter(Boolean).join(', ')
  const meta = [
    location,
    funder.typical_grant && `Typical grant: ${funder.typical_grant}`,
    funder.filing_year && `${funder.filing_year} filing`,
  ].filter(Boolean)
  const matched = funder.why_not?.matched
  const primary = funder.why_not?.reasons?.[0]
  const style = REASON_STYLE[primary] || { variant: 'default', label: 'Not in your digest' }

  return (
    <div className="bg-white rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all">
      <div className="w-full flex items-start gap-3 px-4 py-3">
        <div className="flex-1 min-w-0">
          <Link
            to={`/funders/${funder.funder_id}`}
            className="font-medium text-gray-900 text-sm leading-snug hover:text-brand-500 hover:underline"
          >
            {funder.name}
          </Link>

          {/* Joined, not concatenated with inline separators: a funder with no
              city or state (the IRS files plenty) would otherwise render a
              leading " · ". Corpus search reaches far more sparse records than
              the matched-lead surfaces do, so absent fields are normal here. */}
          {meta.length > 0 && (
            <p className="text-xs text-gray-500 mt-0.5 truncate">
              {meta.join(' · ')}
            </p>
          )}

          {/* The strongest signal in the product, and it runs on funders the
              client has no match for. `known` is false when we hold no EIN,
              and then we say nothing at all rather than implying "no". */}
          {funder.already_funded_known && funder.already_funded && (
            <p className="mt-1.5 text-xs font-medium text-green-700">
              ★ Has funded you
              {funder.already_funded_grants > 1 && <>, {funder.already_funded_grants} grants</>}
              {funder.already_funded_latest_year && <>, most recently {funder.already_funded_latest_year}</>}
              {funder.already_funded_total && <> ({funder.already_funded_total} total)</>}
            </p>
          )}
        </div>

        <div className="flex-shrink-0 flex items-center gap-2">
          {matched && funder.score != null && (
            <div className="text-center">
              <div className="text-lg font-bold text-brand-500 leading-none">{funder.score}</div>
              <div className="text-[10px] text-gray-500 mt-0.5">score</div>
            </div>
          )}
          <Badge variant={style.variant}>{style.label}</Badge>
          <button
            onClick={() => setOpen(o => !o)}
            aria-expanded={open}
            aria-label={open ? 'Hide details' : 'Show details'}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <svg className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`}
              viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>

      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-gray-100 space-y-3">
          {/* The answer to "why isn't this in my digest?", in the server's own
              words. Not reworded here — the phrasing discipline (a 990 records
              where a funder HAS given, never where it would refuse) lives
              server-side and has tests. */}
          <p className="text-xs text-gray-600 leading-relaxed">
            {funder.why_not?.explanation}
          </p>

          {funder.focus_areas?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {funder.focus_areas.map(code => (
                <span key={code}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                  {code}
                </span>
              ))}
            </div>
          )}

          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            {funder.total_giving && (
              <>
                <dt className="text-gray-500">Total giving</dt>
                <dd className="text-gray-900">{funder.total_giving}</dd>
              </>
            )}
            {funder.grants_paid_count != null && (
              <>
                <dt className="text-gray-500">Grants paid</dt>
                <dd className="text-gray-900">{funder.grants_paid_count.toLocaleString()}</dd>
              </>
            )}
            {funder.recipient_states?.length > 0 && (
              <>
                <dt className="text-gray-500">Gives in</dt>
                <dd className="text-gray-900">{funder.recipient_states.slice(0, 8).join(', ')}</dd>
              </>
            )}
          </dl>

          {/* A deadline parsed from Part XV free text describes the cycle at
              FILING time. `deadlines_stale` means it named only past years, so
              it is shown as filed rather than as a live date. */}
          {funder.application_deadlines && (
            <p className="text-xs text-gray-600">
              <span className="text-gray-500">Deadline as filed: </span>
              {funder.application_deadlines}
              {funder.deadlines_stale && (
                <span className="text-amber-700"> (past deadline as filed)</span>
              )}
            </p>
          )}

          <div className="flex items-center gap-3 pt-1">
            {funder.website && (
              <a href={funder.website} target="_blank" rel="noopener noreferrer"
                className="text-xs text-brand-500 hover:underline">
                {funder.website_display || 'Website'} ↗
              </a>
            )}
            {funder.ein_url && (
              <a href={funder.ein_url} target="_blank" rel="noopener noreferrer"
                className="text-xs text-gray-500 hover:underline">
                EIN {funder.ein} ↗
              </a>
            )}
          </div>

          {!matched && (
            <p className="text-[11px] text-gray-400 leading-relaxed">
              Praenum surfaces leads conservatively, so a foundation missing from
              your digest has not been ruled out, it just did not clear the bar
              for a weekly recommendation.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default CorpusFunderCard
